// src/pages/CompPlans.js
import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { SPECTRUM_PRODUCTS, DEFAULT_COMP_PLAN, ROLE_KEYS, ROLE_LABELS, PLAN_TYPES } from '../data/compPlans';
import * as XLSX from 'xlsx';

const TEAL = '#00d4a0';

export default function CompPlans() {
  const [plans, setPlans] = useState([DEFAULT_COMP_PLAN]);
  const [selected, setSelected] = useState(DEFAULT_COMP_PLAN);
  const [editing, setEditing] = useState(false);
  const [editRates, setEditRates] = useState({});
  const [editMeta, setEditMeta] = useState({});
  const [newPlanName, setNewPlanName] = useState('');
  const [newPlanType, setNewPlanType] = useState('Standard');
  const [saving, setSaving] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');

  useEffect(() => {
    async function load() {
      const snap = await getDocs(collection(db, 'compPlans'));
      const dbPlans = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (dbPlans.length) setPlans([DEFAULT_COMP_PLAN, ...dbPlans]);
    }
    load();
  }, []);

  function startEdit() {
    // Build editRates with all new role keys, backfilling old Agent→Rep mapping
    const baseRates = {};
    SPECTRUM_PRODUCTS.forEach(p => {
      const existing = selected.rates?.[p.id] || {};
      baseRates[p.id] = {
        Rep:          existing.Rep    || existing.Agent  || 0,
        Lead:         existing.Lead   || 0,
        Manager:      existing.Manager|| existing.RM     || 0,
        Regional:     existing.Regional || 0,
        AsstRecruiter:existing.AsstRecruiter || 0,
        Recruiter:    existing.Recruiter || 0,
        Director:     existing.Director || 0,
      };
    });
    setEditRates(baseRates);
    setEditMeta({ name: selected.name, planType: selected.planType || 'Standard', carrier: selected.carrier || 'Spectrum' });
    setEditing(true);
  }

  function updateRate(productId, roleKey, val) {
    setEditRates(r => ({ ...r, [productId]: { ...r[productId], [roleKey]: +val } }));
  }

  async function saveEdit() {
    setSaving(true);
    const updated = { ...selected, ...editMeta, rates: editRates };
    if (selected.id === DEFAULT_COMP_PLAN.id) {
      // Default plan has no Firestore doc yet — create one so edits persist
      const ref = await addDoc(collection(db, 'compPlans'), { name: updated.name, carrier: updated.carrier || 'Spectrum', planType: updated.planType || 'Standard', rates: editRates });
      const persisted = { ...updated, id: ref.id };
      setPlans(prev => prev.map(p => p.id === selected.id ? persisted : p));
      setSelected(persisted);
    } else {
      await updateDoc(doc(db, 'compPlans', selected.id), { rates: editRates, ...editMeta });
      setPlans(prev => prev.map(p => p.id === selected.id ? updated : p));
      setSelected(updated);
    }
    setEditing(false);
    setSaving(false);
  }

  async function createPlan() {
    if (!newPlanName.trim()) return;
    const emptyRates = {};
    SPECTRUM_PRODUCTS.forEach(p => {
      emptyRates[p.id] = { Rep: 0, Lead: 0, Manager: 0, Regional: 0, AsstRecruiter: 0, Recruiter: 0, Director: 0 };
    });
    const newPlan = { name: newPlanName, carrier: 'Spectrum', planType: newPlanType, rates: emptyRates };
    const ref = await addDoc(collection(db, 'compPlans'), newPlan);
    const plan = { id: ref.id, ...newPlan };
    setPlans(prev => [...prev, plan]);
    setSelected(plan);
    setNewPlanName('');
    startEditPlan(plan);
  }

  function startEditPlan(plan) {
    const baseRates = {};
    SPECTRUM_PRODUCTS.forEach(p => {
      const existing = plan.rates?.[p.id] || {};
      baseRates[p.id] = {
        Rep: existing.Rep || existing.Agent || 0,
        Lead: existing.Lead || 0,
        Manager: existing.Manager || existing.RM || 0,
        Regional: existing.Regional || 0,
        AsstRecruiter: existing.AsstRecruiter || 0,
        Recruiter: existing.Recruiter || 0,
        Director: existing.Director || 0,
      };
    });
    setEditRates(baseRates);
    setEditMeta({ name: plan.name, planType: plan.planType || 'Standard', carrier: plan.carrier || 'Spectrum' });
    setEditing(true);
  }

  // Download Excel template
  function downloadTemplate() {
    const headers = ['Product', ...ROLE_KEYS.map(r => ROLE_LABELS[r])];
    const rows = SPECTRUM_PRODUCTS.map(p => {
      const rates = selected.rates?.[p.id] || {};
      return [p.name, ...ROLE_KEYS.map(r => rates[r] || 0)];
    });
    const ws = XLSX.utils.aoa_to_sheet([
      ['Comp Plan Template — ' + selected.name],
      ['Plan Type', selected.planType || 'Standard'],
      ['Carrier', selected.carrier || 'Spectrum'],
      [],
      headers,
      ...rows,
    ]);
    ws['!cols'] = [{ wch: 40 }, ...ROLE_KEYS.map(() => ({ wch: 14 }))];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Comp Plan');
    XLSX.writeFile(wb, `comp_plan_template_${selected.name.replace(/\s+/g, '_')}.xlsx`);
  }

  // Upload Excel comp plan
  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadStatus('Parsing...');
    const reader = new FileReader();
    reader.onload = async ev => {
      try {
        const wb = XLSX.read(new Uint8Array(ev.target.result), { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: 0 });

        // Find header row (contains 'Product')
        let headerIdx = rows.findIndex(r => String(r[0]).toLowerCase().includes('product'));
        if (headerIdx === -1) { setUploadStatus('Error: Could not find Product column'); return; }

        // Get plan meta from rows above header
        let planName = selected.name;
        let planType = 'Standard';
        let carrier = 'Spectrum';
        for (let i = 0; i < headerIdx; i++) {
          const row = rows[i];
          if (String(row[0]).toLowerCase().includes('plan type')) planType = String(row[1] || 'Standard');
          if (String(row[0]).toLowerCase().includes('carrier')) carrier = String(row[1] || 'Spectrum');
          if (String(row[0]).toLowerCase().includes('template')) {
            const match = String(row[0]).match(/—\s*(.+)$/);
            if (match) planName = match[1].trim();
          }
        }

        const headers = rows[headerIdx].map(h => String(h).trim());
        const dataRows = rows.slice(headerIdx + 1).filter(r => r[0]);
        const newRates = {};

        dataRows.forEach(row => {
          const productName = String(row[0]).trim();
          const product = SPECTRUM_PRODUCTS.find(p =>
            p.name.toLowerCase() === productName.toLowerCase() ||
            p.id === productName.toLowerCase().replace(/[^a-z0-9]/g, '_')
          );
          if (!product) return;
          newRates[product.id] = {};
          ROLE_KEYS.forEach(rk => {
            const label = ROLE_LABELS[rk];
            const colIdx = headers.findIndex(h => h.toLowerCase() === label.toLowerCase());
            newRates[product.id][rk] = colIdx >= 0 ? Number(row[colIdx]) || 0 : 0;
          });
        });

        if (selected.id === DEFAULT_COMP_PLAN.id) {
          // Save as new plan
          const ref = await addDoc(collection(db, 'compPlans'), { name: planName, carrier, planType, rates: newRates });
          const plan = { id: ref.id, name: planName, carrier, planType, rates: newRates };
          setPlans(prev => [...prev, plan]);
          setSelected(plan);
        } else {
          await updateDoc(doc(db, 'compPlans', selected.id), { rates: newRates, planType, carrier });
          const updated = { ...selected, rates: newRates, planType, carrier };
          setPlans(prev => prev.map(p => p.id === selected.id ? updated : p));
          setSelected(updated);
        }
        setUploadStatus(`✓ Imported ${Object.keys(newRates).length} products successfully`);
        e.target.value = '';
      } catch(err) {
        setUploadStatus('Error: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  }

  const displayRates = editing ? editRates : (() => {
    // Normalize old rates for display
    const r = {};
    SPECTRUM_PRODUCTS.forEach(p => {
      const existing = selected.rates?.[p.id] || {};
      r[p.id] = {
        Rep: existing.Rep || existing.Agent || 0,
        Lead: existing.Lead || 0,
        Manager: existing.Manager || existing.RM || 0,
        Regional: existing.Regional || 0,
        AsstRecruiter: existing.AsstRecruiter || 0,
        Recruiter: existing.Recruiter || 0,
        Director: existing.Director || 0,
      };
    });
    return r;
  })();

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Comp plans</h1>
          <p style={S.sub}>Manage commission rates per product and role</p>
        </div>
      </div>

      <div className="layout-grid" style={S.layout}>
        {/* Plan list */}
        <div style={S.planList}>
          <div style={S.planListTitle}>Plans</div>
          {plans.map(p => (
            <div key={p.id}
              style={{ ...S.planItem, ...(selected.id === p.id ? S.planItemActive : {}) }}
              onClick={() => { setSelected(p); setEditing(false); }}>
              <div>{p.name}</div>
              {p.planType === 'ISO' && <span style={S.isoBadge}>ISO</span>}
            </div>
          ))}
          <div style={S.newPlanSection}>
            <input style={{ ...S.inp, marginBottom: 6 }} placeholder="New plan name..." value={newPlanName}
              onChange={e => setNewPlanName(e.target.value)} onKeyDown={e => e.key === 'Enter' && createPlan()} />
            <div style={{ display: 'flex', gap: 6 }}>
              <select style={{ ...S.inp, flex: 1, fontSize: 11 }} value={newPlanType} onChange={e => setNewPlanType(e.target.value)}>
                {PLAN_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
              <button style={S.createBtn} onClick={createPlan}>+</button>
            </div>
          </div>
        </div>

        {/* Plan detail */}
        <div style={S.planDetail}>
          <div style={S.detailHeader}>
            <div>
              <div style={S.planName}>{editing ? editMeta.name : selected.name}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <span style={{ fontSize: 11, color: '#555' }}>{selected.carrier || 'Spectrum'}</span>
                <span style={{ ...S.typeBadge, background: selected.planType === 'ISO' ? '#2a1a2e' : '#1a2e24', color: selected.planType === 'ISO' ? '#a78bfa' : TEAL }}>
                  {selected.planType || 'Standard'}
                </span>
              </div>
            </div>
            <div style={S.detailActions}>
              {/* Upload */}
              <label style={S.uploadBtn}>
                ↑ Upload Excel
                <input type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleUpload} />
              </label>
              {/* Download template */}
              <button style={S.templateBtn} onClick={downloadTemplate}>↓ Template</button>
              {editing ? (
                <>
                  <button style={S.cancelBtn} onClick={() => setEditing(false)}>Cancel</button>
                  <button style={S.saveBtn} onClick={saveEdit} disabled={saving}>{saving ? 'Saving...' : 'Save rates'}</button>
                </>
              ) : (
                <button style={S.editBtn} onClick={startEdit}>Edit rates</button>
              )}
            </div>
          </div>

          {uploadStatus && (
            <div style={{ ...S.uploadStatus, color: uploadStatus.startsWith('✓') ? TEAL : '#ff6b6b' }}>
              {uploadStatus}
            </div>
          )}

          {selected.planType === 'ISO' && (
            <div style={S.isoInfo}>
              ISO plan — all commission for orders under this plan is paid to the ISO entity (first earner on the deal). Individual rep/override rates are not applicable.
            </div>
          )}

          {editing && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={S.label}>Plan name</label>
                <input style={S.inp} value={editMeta.name} onChange={e => setEditMeta(m => ({ ...m, name: e.target.value }))} />
              </div>
              <div>
                <label style={S.label}>Plan type</label>
                <select style={S.inp} value={editMeta.planType} onChange={e => setEditMeta(m => ({ ...m, planType: e.target.value }))}>
                  {PLAN_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Product</th>
                  {ROLE_KEYS.map(r => <th key={r} style={S.th}>{ROLE_LABELS[r]}</th>)}
                  <th style={S.th}>Total payout</th>
                </tr>
              </thead>
              <tbody>
                {SPECTRUM_PRODUCTS.map(product => {
                  const rates = displayRates[product.id] || {};
                  const total = ROLE_KEYS.reduce((a, r) => a + (rates[r] || 0), 0);
                  return (
                    <tr key={product.id}>
                      <td style={S.td}>{product.name}</td>
                      {ROLE_KEYS.map(r => (
                        <td key={r} style={S.td}>
                          {editing ? (
                            <input style={S.rateInput} type="number" min={0}
                              value={editRates[product.id]?.[r] || 0}
                              onChange={e => updateRate(product.id, r, e.target.value)} />
                          ) : (
                            <span style={{ color: (rates[r] || 0) > 0 ? TEAL : '#333' }}>
                              ${rates[r] || 0}
                            </span>
                          )}
                        </td>
                      ))}
                      <td style={{ ...S.td, fontWeight: 600, color: '#f5a623' }}>${total}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 12, color: '#555', margin: '4px 0 0' },
  layout: { display: 'grid', gridTemplateColumns: '220px 1fr', gap: 14 },
  planList: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10, padding: '14px', display: 'flex', flexDirection: 'column', gap: 3 },
  planListTitle: { fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, marginBottom: 6 },
  planItem: { padding: '8px 10px', borderRadius: 7, fontSize: 12, color: '#888', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  planItemActive: { background: '#1a1a28', color: TEAL },
  isoBadge: { fontSize: 9, background: '#2a1a2e', color: '#a78bfa', padding: '1px 6px', borderRadius: 4, fontWeight: 600 },
  newPlanSection: { marginTop: 10, paddingTop: 10, borderTop: '1px solid #1e1e2a' },
  inp: { width: '100%', background: '#1a1a24', border: '1px solid #2a2a3a', borderRadius: 7, padding: '7px 10px', color: '#fff', fontSize: 12, outline: 'none', boxSizing: 'border-box' },
  createBtn: { background: TEAL, border: 'none', borderRadius: 7, padding: '7px 12px', color: '#000', fontWeight: 700, cursor: 'pointer', fontSize: 15 },
  planDetail: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10, padding: '18px 20px' },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  planName: { fontSize: 16, fontWeight: 600 },
  typeBadge: { fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 500 },
  detailActions: { display: 'flex', gap: 7, flexWrap: 'wrap', justifyContent: 'flex-end' },
  uploadBtn: { background: '#1a1a28', border: '1px solid #2a2a3a', borderRadius: 7, padding: '6px 12px', color: '#888', fontSize: 11, cursor: 'pointer' },
  templateBtn: { background: 'transparent', border: '1px solid #2a2a3a', borderRadius: 7, padding: '6px 12px', color: '#5599cc', fontSize: 11, cursor: 'pointer' },
  editBtn: { background: 'transparent', border: '1px solid #2a2a3a', borderRadius: 7, padding: '6px 12px', color: '#888', fontSize: 12, cursor: 'pointer' },
  cancelBtn: { background: 'transparent', border: '1px solid #2a2a3a', borderRadius: 7, padding: '6px 12px', color: '#888', fontSize: 12, cursor: 'pointer' },
  saveBtn: { background: TEAL, border: 'none', borderRadius: 7, padding: '6px 14px', color: '#000', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  uploadStatus: { fontSize: 12, padding: '8px 12px', background: '#0f0f18', borderRadius: 7, marginBottom: 12 },
  isoInfo: { fontSize: 12, color: '#888', background: '#1a1020', border: '1px solid #2a1a3a', borderRadius: 7, padding: '10px 12px', marginBottom: 14, lineHeight: 1.6 },
  label: { display: 'block', fontSize: 10, color: '#555', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 700 },
  th: { textAlign: 'left', padding: '7px 10px', fontSize: 10, color: '#555', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #1e1e2a', whiteSpace: 'nowrap' },
  td: { padding: '9px 10px', fontSize: 12, color: '#ccc', borderBottom: '1px solid #111' },
  rateInput: { background: '#0f0f18', border: '1px solid #2a2a3a', borderRadius: 5, padding: '4px 7px', color: TEAL, fontSize: 12, width: 65, outline: 'none' },
};
